import { html, nothing, unsafeCSS, type CSSResultGroup, type PropertyValues } from 'lit';
import { live } from 'lit/directives/live.js';
import { customElement, property, state } from 'lit/decorators.js';
import { LibElement } from '../lib-element';
import css from './lib-textarea.css';

/** A multiline text editor with validation feedback and configurable commit timing. */
@customElement('lib-textarea')
export class LibTextArea extends LibElement {
  protected override get hostFocusSelector(): string { return 'textarea'; }
  static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(css)];

  /** Participates in the surrounding form through ElementInternals, like lib-textbox. */
  static readonly formAssociated = true;
  readonly #internals?: ElementInternals;

  constructor() {
    super();
    try {
      this.#internals = this.attachInternals();
    } catch {
      this.#internals = undefined;
    }
  }

  /** Form field name; the text the user currently sees is submitted under it. */
  @property() name = '';
  /** Id of the shadow textarea, so `<label for>` and automation selectors can reach it. */
  @property({ attribute: 'input-id' }) inputId = '';
  /** Autofill hint forwarded to the shadow textarea. */
  @property() autocomplete = '';
  /** Focuses the editor once it has rendered. */
  @property({ type: Boolean }) autofocus = false;

  @property() value = '';
  private revision = 0;
  @property() headline = '';
  @property() placeholder = '';
  @property({ type: Number }) maxlength = 0;
  @property({ type: Number }) rows = 4;
  @property({ type: Boolean, attribute: 'show-maxlength' }) showMaxlength = false;
  @property({ type: Boolean }) disabled = false;
  @property({ type: Boolean }) readonly = false;
  @property({ type: Boolean, attribute: 'no-resize' }) noResize = false;
  @property({ attribute: 'bind-mode' }) bindMode = 'oninput';
  @property({ type: Boolean }) error = false;
  @property() message = '';
  @property() surface = 'white';
  @property({ attribute: 'aria-label' }) override ariaLabel: string | null = null;
  @state() private draft = '';
  @state() private formDisabled = false;
  private composing = false;
  private timer?: ReturnType<typeof setTimeout>;
  private pendingValues: string[] = [];

  /** Disabled by the attribute or by an enclosing disabled fieldset. */
  private get effectivelyDisabled(): boolean { return this.disabled || this.formDisabled; }

  private get textareaElement(): HTMLTextAreaElement | null {
    return this.renderRoot.querySelector<HTMLTextAreaElement>('textarea');
  }

  static override get observedAttributes(): string[] { return [...super.observedAttributes, 'server-state']; }

  override attributeChangedCallback(name: string, previous: string | null, value: string | null): void {
    if (name === 'server-state') {
      if (value !== null) {
        const state = JSON.parse(value) as { value: string; revision: number };
        if (state.revision >= this.revision) this.value = state.value;
      }
      return;
    }
    if (name === 'value' && this.hasAttribute('server-state')) return;
    if (name === 'value') {
      const pending = this.pendingValues.indexOf(value ?? '');
      if (pending >= 0) {
        this.pendingValues.splice(0, pending + 1);
        return;
      }
      this.pendingValues = [];
    }
    super.attributeChangedCallback(name, previous, value);
  }

  protected override willUpdate(changed: PropertyValues<this>): void {
    if (changed.has('value')) {
      this.cancelPending();
      this.draft = this.value;
    }
    if (changed.has('disabled') || changed.has('readonly') || changed.has('bindMode'))
      this.cancelPending();
    this.syncFormValue();
  }

  protected override firstUpdated(): void {
    if (!this.autofocus) return;
    void this.updateComplete.then(() => {
      if (!this.effectivelyDisabled) this.textareaElement?.focus();
    });
  }

  override disconnectedCallback(): void {
    this.cancelPending();
    this.composing = false;
    this.pendingValues = [];
    super.disconnectedCallback();
  }

  private cancelPending(): void {
    clearTimeout(this.timer);
    this.timer = undefined;
  }

  private commit(): void {
    this.cancelPending();
    if (this.effectivelyDisabled || this.readonly || this.composing || this.value === this.draft) return;
    this.value = this.draft;
    if (this.hasAttribute('value') && !this.hasAttribute('server-state')) this.pendingValues.push(this.value);
    this.notifyChange();
  }

  private notifyChange(): void {
    this.dispatchEvent(new CustomEvent('lib-textarea-change', {
      detail: { value: this.value, revision: this.revision }, bubbles: true, composed: true,
    }));
    if (!this.hasAttribute('server-state'))
      this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }

  private input(event: Event): void {
    event.stopPropagation();
    if (this.effectivelyDisabled || this.readonly) return;
    this.revision++;
    this.draft = (event.target as HTMLTextAreaElement).value;
    this.cancelPending();
    if (this.composing) return;
    if (this.bindMode === 'oninput') this.commit();
    else if (this.bindMode === 'ondelayedinput')
      this.timer = setTimeout(() => this.commit(), 300);
  }

  private change(event: Event): void {
    event.stopPropagation();
    this.commit();
  }

  /** Restores the value attribute - for example the server-rendered prefill - on form reset. */
  formResetCallback(): void {
    const initial = this.getAttribute('value') ?? '';
    this.cancelPending();
    this.composing = false;
    this.pendingValues = [];
    const changed = this.draft !== initial || this.value !== initial;
    this.revision++;
    this.draft = initial;
    this.value = initial;
    this.syncFormValue();
    if (changed) this.notifyChange();
  }

  /** Mirrors a disabled fieldset onto the shadow textarea and the submitted value. */
  formDisabledCallback(disabled: boolean): void {
    this.formDisabled = disabled;
    this.syncFormValue();
  }

  /** Submits the visible draft, so a form post never waits for the bind mode to commit. */
  private syncFormValue(): void {
    try {
      this.#internals?.setFormValue(this.effectivelyDisabled ? null : this.draft);
    } catch {
      // Engines without form-associated custom elements keep every other feature working.
    }
  }

  override render() {
    const disabled = this.effectivelyDisabled;
    const inputId = this.inputId || 'input';
    const invalid = this.error || !!this.message;
    const classes = `lib-textarea lib-textarea-surface-${this.surface} ${disabled ? 'lib-textarea-disabled' : ''} ${this.readonly ? 'lib-textarea-readonly' : ''} ${invalid ? 'lib-textarea-error' : ''}`;
    return html`<div class=${classes}>
      ${this.headline ? html`<div class="lib-textarea-label">
        <label for=${inputId}>${this.headline}</label><slot name="help"></slot>
      </div>` : nothing}
      <div class="lib-textarea-field">
        <textarea id=${inputId} name=${this.name || nothing} autocomplete=${this.autocomplete || nothing}
          .value=${live(this.draft)} rows=${Math.max(1, Math.floor(this.rows) || 1)}
          placeholder=${this.placeholder} maxlength=${this.maxlength > 0 ? this.maxlength : nothing}
          ?disabled=${disabled} ?readonly=${this.readonly}
          style=${`resize: ${this.noResize || disabled || this.readonly ? 'none' : 'vertical'}`}
          aria-label=${this.ariaLabel || nothing} aria-invalid=${invalid ? 'true' : nothing}
          aria-describedby=${this.message ? 'message' : nothing}
          @input=${this.input} @change=${this.change} @blur=${this.commit}
          @compositionstart=${() => { this.composing = true; this.cancelPending(); }}
          @compositionend=${(event: CompositionEvent) => { this.composing = false; this.input(event); }}></textarea>
      </div>
      ${this.showMaxlength && this.maxlength > 0 ? html`<div class="lib-textarea-counter">${this.draft.length}/${this.maxlength}</div>` : nothing}
      ${this.message ? html`<div id="message" class="lib-textarea-message">${this.message}</div>` : nothing}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'lib-textarea': LibTextArea; }
}
